import React, { useEffect, useMemo, useState } from 'react';
import { App, Button, Space, type ButtonProps } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { canManageProductCatalog } from '../../modules/products/productCatalogAccessPolicy';
import {
  fetchProductSelectionVariant,
  type ProductSelectionPurchasePriceScope,
} from '../../modules/products/productSelectionApi';
import { useProductSelectionCatalog } from '../../modules/products/useProductSelectionCatalog';
import { authService } from '../../services/auth';
import {
  getProductSelectionLabel,
  getProductSelectorDimensions,
} from '../../utils/productSelectorLabel';
import type { Inventory, Product, ProductVariant } from '../../types/pz';
import AddProductModal from './AddProductModal';
import ProductPickerModal from './ProductPickerModal';
import './VariantPicker.css';

interface VariantWithInventory extends ProductVariant {
  inventory?: Inventory;
}

export interface VariantPickerChangeContext {
  reason: 'terminal-selection' | 'clear';
  previousVariant?: VariantWithInventory;
}

interface VariantPickerProps {
  value?: string;
  onChange?: (
    catalogItemId: string | undefined,
    variant?: VariantWithInventory,
    context?: VariantPickerChangeContext,
  ) => void;
  disabled?: boolean;
  placeholder?: string;
  showInventory?: boolean;
  filterAvailable?: boolean;
  style?: React.CSSProperties;
  size?: ButtonProps['size'];
  allowCreate?: boolean;
  /**
   * Short action copy for compound fields which render the selected product
   * identity alongside the picker. The default remains the full business
   * label so warehouse consumers keep their current presentation.
   */
  triggerLabel?: string;
  /**
   * Label for a historical line which still has no catalog relation. It is
   * rendered inside the same picker button so legacy orders keep a readable
   * product identity without bringing back a second, competing name field.
   */
  unlinkedLabel?: string;
  purchasePriceScope?: ProductSelectionPurchasePriceScope;
  'data-testid'?: string;
}

const VariantPicker: React.FC<VariantPickerProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  showInventory = true,
  filterAvailable = false,
  style,
  size,
  allowCreate = false,
  triggerLabel,
  unlinkedLabel,
  purchasePriceScope,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation(['products', 'common']);
  const { message } = App.useApp();
  const { variants, loading, reload } = useProductSelectionCatalog(true, purchasePriceScope);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [historicalVariant, setHistoricalVariant] = useState<ProductVariant>();

  const currentUser = authService.getCurrentUser();
  const canCreate = allowCreate && canManageProductCatalog(currentUser);

  const selectedVariant = useMemo(
    () => variants.find((variant) => variant.id === value) || historicalVariant,
    [historicalVariant, value, variants],
  );

  useEffect(() => {
    if (!value || variants.some((variant) => variant.id === value)) {
      setHistoricalVariant(undefined);
      return;
    }
    fetchProductSelectionVariant(value, purchasePriceScope)
      .then(setHistoricalVariant)
      .catch(() => setHistoricalVariant(undefined));
  }, [purchasePriceScope, value, variants]);

  const selectedLabel = triggerLabel || (selectedVariant
    ? [
        getProductSelectionLabel(selectedVariant, t('products:catalogPicker.unnamedProduct')),
        getProductSelectorDimensions(selectedVariant),
      ].filter(Boolean).join(' – ')
    : unlinkedLabel || placeholder || t('products:catalogPicker.openButton'));

  const handleProductCreated = async (product: Product) => {
    setCreateOpen(false);
    const refreshed = await reload();
    const createdVariants = refreshed.filter((variant) => variant.productId === product.id);
    const defaultVariant = createdVariants.find((variant) => variant.isDefault)
      || createdVariants.find((variant) => variant.variantName === 'Standard')
      || createdVariants[0];
    if (!defaultVariant) {
      message.warning(t('products:addProductModal.variantNotFound'));
      return;
    }
    onChange?.(defaultVariant.id, defaultVariant, {
      reason: 'terminal-selection',
      previousVariant: selectedVariant,
    });
  };

  return (
    <>
      <Space.Compact className="variant-picker-field" style={style}>
        <Button
          block
          size={size}
          disabled={disabled}
          loading={loading && !selectedVariant}
          icon={<SearchOutlined aria-hidden="true" />}
          onClick={() => setPickerOpen(true)}
          data-testid={dataTestId}
          className="variant-picker-field__select"
        >
          <span className="variant-picker-field__label">{selectedLabel}</span>
        </Button>
        {value && !disabled && (
          <Button
            size={size}
            icon={<CloseOutlined aria-hidden="true" />}
            aria-label={t('common:clear')}
            onClick={() => onChange?.(undefined, undefined, {
              reason: 'clear',
              previousVariant: selectedVariant,
            })}
          />
        )}
      </Space.Compact>

      <ProductPickerModal
        open={pickerOpen}
        variants={variants}
        loading={loading}
        selectedVariantId={value}
        showInventory={showInventory}
        filterAvailable={filterAvailable}
        canCreate={canCreate}
        onRequestCreate={() => {
          setPickerOpen(false);
          setCreateOpen(true);
        }}
        onSelect={(variant) => {
          onChange?.(variant.id, variant, {
            reason: 'terminal-selection',
            previousVariant: selectedVariant,
          });
          setPickerOpen(false);
        }}
        onCancel={() => setPickerOpen(false)}
      />

      {canCreate && (
        <AddProductModal
          open={createOpen}
          onCancel={() => setCreateOpen(false)}
          onCreated={handleProductCreated}
        />
      )}
    </>
  );
};

export default VariantPicker;
